import React, { useMemo } from 'react';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Search,
  MessageSquare,
  FileText,
  Hash
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { type Snippet } from '@/services/snippets.service';
import { formatDate } from '@/lib/format';
import { useTranslation } from 'react-i18next';

// Helper function to strip HTML tags and decode entities for preview
const stripHtml = (html: string): string => {
  if (!html) return '';
  // Create a temporary element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  // Get text content and normalize whitespace
  return tmp.textContent || tmp.innerText || '';
};

interface SnippetDataTableProps {
  snippets: Snippet[];
  isLoading: boolean;
  onEdit: (snippet: Snippet) => void;
  onDelete: (id: string) => void;
  isDeletePending?: boolean;
}

export function SnippetDataTable({ 
  snippets, 
  isLoading, 
  onEdit, 
  onDelete,
  isDeletePending 
}: SnippetDataTableProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);

  // Filter snippets
  const filteredSnippets = useMemo(() => {
    return snippets.filter(snippet => {
      const matchesSearch = 
        snippet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        snippet.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        snippet.shortcut.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || snippet.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [snippets, searchTerm, categoryFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredSnippets.length / itemsPerPage);
  const paginatedSnippets = filteredSnippets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, itemsPerPage]);

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, string> = {
      general: 'Allgemein',
      greeting: 'Begrüßung',
      closing: 'Abschluss',
      shipping: 'Versand',
      refund: 'Rückerstattung',
      support: 'Support'
    };
    return categories[category] || category;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'greeting':
      case 'closing':
        return 'bg-emerald-100 text-emerald-700';
      case 'shipping':
        return 'bg-blue-100 text-blue-700';
      case 'refund':
        return 'bg-amber-100 text-amber-700';
      case 'support':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800" />
          <p className="text-sm text-slate-500">{t('common:x1.loadingSnippets2', 'Textbausteine werden geladen...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex flex-1 items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('common:x1.searchDots', 'Suchen...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 bg-white border-slate-200 focus-visible:ring-slate-200"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] h-9 bg-white border-slate-200 focus:ring-slate-200">
              <SelectValue placeholder={t('common:field.category', 'Kategorie')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common:x1.allCategories', 'Alle Kategorien')}</SelectItem>
              <SelectItem value="general">{t('common:field.general', 'Allgemein')}</SelectItem>
              <SelectItem value="greeting">{t('common:field.greeting', 'Begrüßung')}</SelectItem>
              <SelectItem value="closing">{t('common:field.closing', 'Abschluss')}</SelectItem>
              <SelectItem value="shipping">{t('agent:dept.shipping', 'Versand')}</SelectItem>
              <SelectItem value="refund">{t('settings:p2.refund', 'Rückerstattung')}</SelectItem>
              <SelectItem value="support">{t('settings:p2.support', 'Support')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="border-t border-slate-100">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-6 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[250px]">
                {t('common:x1.nameShortcut', 'Name & Shortcut')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('common:x1.contentPreview', 'Inhalt (Vorschau)')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[140px]">
                {t('common:field.category', 'Kategorie')}
              </th>
              <th className="py-3 px-4 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[120px]">
                {t('common:x1.usage2', 'Verwendung')}
              </th>
              <th className="py-3 px-6 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-[100px]">
                {t('common:field.actions', 'Aktionen')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedSnippets.map((snippet) => (
              <tr key={snippet.id} className="group hover:bg-slate-50/50 transition-colors">
                <td className="py-4 px-6 align-top">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-900">{snippet.name}</p>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="font-mono text-[10px] bg-slate-50 text-slate-600 border-slate-200 px-1.5 h-5">
                        {snippet.shortcut}
                      </Badge>
                      {snippet.isGlobal && (
                        <Badge variant="secondary" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-100 px-1.5 h-5">
                          {t('common:x1.globalLbl', 'Global')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4 align-top">
                  <div className="max-w-xl">
                    <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">
                      {stripHtml(snippet.content)}
                    </p>
                  </div>
                </td>
                <td className="py-4 px-4 align-top">
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(snippet.category)}`}>
                    {getCategoryLabel(snippet.category)}
                  </span>
                </td>
                <td className="py-4 px-4 align-top">
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-slate-700">
                      {snippet.usageCount || 0} mal
                    </div>
                    {snippet.lastUsedAt && (
                      <div className="text-[10px] text-slate-400">
                        {t('common:x1.lastColon', 'Zuletzt:')} {formatDate(snippet.lastUsedAt)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6 align-top text-right">
                  <div className="flex items-center justify-end gap-1 opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                      onClick={() => navigator.clipboard.writeText(snippet.content)}
                      title={t('ticket:action.copy', 'Kopieren')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-900 hover:bg-slate-100"
                      onClick={() => onEdit(snippet)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl border-0 shadow-xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('common:x1.deleteSnippet', 'Textbaustein löschen')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('common:x1.areYouSureThat', 'Sind Sie sicher, dass Sie "')}{snippet.name}" löschen möchten? 
                            Diese Aktion kann nicht rückgängig gemacht werden.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-lg border-slate-200">{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => onDelete(snippet.id)}
                            className="bg-red-600 hover:bg-red-700 rounded-lg text-white"
                          >
                            {t('common:action.delete', 'Löschen')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredSnippets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-4">
              <MessageSquare className="h-6 w-6 text-slate-300" />
            </div>
            <h3 className="text-sm font-medium text-slate-900">{t('common:x1.noSnippetsFound', 'Keine Textbausteine gefunden')}</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-xs">
              {searchTerm || categoryFilter !== 'all' 
                ? 'Versuchen Sie es mit anderen Suchbegriffen oder Filtern.'
                : 'Erstellen Sie Ihren ersten Textbaustein, um Zeit zu sparen.'}
            </p>
          </div>
        )}

        {/* Pagination Footer */}
        {filteredSnippets.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/30">
            <div className="text-xs text-slate-500">
              {t('common:x1.showing', 'Zeige')} {(currentPage - 1) * itemsPerPage + 1} bis {Math.min(currentPage * itemsPerPage, filteredSnippets.length)} von {filteredSnippets.length} Einträgen
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">{t('common:action.back', 'Zurück')}</span>
                ←
              </Button>
              <div className="text-xs font-medium text-slate-700">
                {t('common:field.page', 'Seite')} {currentPage} von {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">{t('common:action.next', 'Weiter')}</span>
                →
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
